import { createContext, useContext } from 'react'
import { useSelector } from '@xstate/react'
import type { ActorRefFrom, SnapshotFrom } from 'xstate'
import { checkInMachine, type CheckInContext } from './machine'
import { checkInSelectors } from './selectors'

// Types for the actor
type CheckInActor = ActorRefFrom<typeof checkInMachine>
type CheckInSnapshot = SnapshotFrom<typeof checkInMachine>

// Context for the check-in actor
const CheckInActorContext = createContext<CheckInActor | null>(null)

/**
 * Provider component - wraps the flow and provides the actor to children
 */
export function CheckInActorProvider({
  actor,
  children
}: {
  actor: CheckInActor
  children: React.ReactNode
}) {
  return <CheckInActorContext.Provider value={actor}>{children}</CheckInActorContext.Provider>
}

/**
 * Hook to get the check-in actor for sending events
 */
export function useCheckInActor(): CheckInActor {
  const actor = useContext(CheckInActorContext)
  if (!actor) {
    throw new Error('useCheckInActor must be used within CheckInActorProvider')
  }
  return actor
}

/**
 * Hook to send events to the check-in machine
 */
export function useCheckInSend() {
  const actor = useCheckInActor()
  return actor.send
}

/**
 * Hook to select specific parts of the check-in state
 * Only re-renders when the selected value changes
 */
export function useCheckInSelector<T>(selector: (snapshot: CheckInSnapshot) => T): T {
  const actor = useCheckInActor()
  return useSelector(actor, selector)
}

/**
 * Hook to get the current check-in context
 */
export function useCheckInContext(): CheckInContext {
  return useCheckInSelector(checkInSelectors.context)
}
